import React from "react";
import {Alert, Box, Button, Chip, Link, Paper, Stack, Typography} from "@mui/material";
import {Link as RouterLink} from "react-router-dom";
import ComposicaoDaFatura from "../../../shared/ui/ComposicaoDaFatura";
import {formatReais} from "../../../shared/utils/formatters";
import {getStatusColor, getStatusLabel} from "../licencasUtils";
import {
    dataBR,
    descontoVale,
    descricaoDoDesconto,
    efeitoNoAcesso,
    getMeioLabel,
    linhaDoTempo,
    SEM_CICLO_PAGO,
    vigente
} from "../assinaturasUtils";

const SITUACAO_VERSAO = {A_VENDA: ['à venda', 'success'], EM_USO: ['fora de venda', 'warning'], ENCERRADA: ['encerrada', 'default'], RASCUNHO: ['rascunho', 'info']};

function Cartao({titulo, extra, children, rodape}) {
    return (
        <Paper variant="outlined" component="section" aria-label={titulo}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{px: {xs: 2, md: 2.5}, pt: 2, pb: 1}} flexWrap="wrap" useFlexGap>
                <Typography variant="h6" component="h2" fontWeight={700}>{titulo}</Typography>
                {extra}
            </Stack>
            <Box sx={{px: {xs: 2, md: 2.5}, pb: 2}}>{children}</Box>
            {rodape ? <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap sx={{px: {xs: 2, md: 2.5}, py: 1.5, borderTop: 1, borderColor: 'divider'}}>{rodape}</Stack> : null}
        </Paper>
    );
}

function Item({rotulo, children}) {
    return (
        <>
            <Typography component="dt" variant="body2" color="text.secondary">{rotulo}</Typography>
            <Box component="dd" sx={{m: 0, fontSize: 14}}>{children}</Box>
        </>
    );
}

const Acoes = ({children}) => <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{mt: 0.5}}>{children}</Stack>;
const BotaoLink = (props) => <Button size="small" sx={{px: 0.5, minWidth: 0, textTransform: 'none'}} {...props} />;

/*
 * Visão geral da assinatura (protótipo v2): contrato, situação e acesso, próxima fatura e histórico. O tenant é só
 * leitura. Cada ação abre um diálogo que mostra o efeito antes de confirmar.
 */
export default function VisaoGeralDaAssinatura({licenca, empresa, versaoDetalhe, versaoAVenda, historico, nomesDeModulo, previa, acoes}) {
    const ativa = vigente(licenca);
    const versao = licenca.versao || {};
    const situacaoVersao = SITUACAO_VERSAO[versaoDetalhe?.situacao || versao.situacao];
    const podeMigrar = ativa && versaoAVenda && versaoAVenda.id !== licenca.id_plano_versao && !licenca.troca_agendada && !licenca.cancelamento
        && licenca.status !== 'AGUARDANDO_CARTAO' && versaoAVenda.id_plano === licenca.id_plano;
    const acesso = efeitoNoAcesso(licenca.status);
    const linhas = linhaDoTempo(historico, nomesDeModulo);
    const desconto = licenca.desconto;

    // Com o cancelamento agendado não há botão: desistir fica no próprio item. Sem botão, sem rodapé.
    const botoesDoContrato = ativa ? [
        licenca.cancelamento ? null : <Button key="cancelar" color="error" variant="outlined" onClick={acoes.cancelar}>Cancelar assinatura</Button>,
        licenca.status === 'AGUARDANDO_CARTAO' || licenca.cancelamento ? null : <Button key="trocar" variant="outlined" onClick={() => acoes.trocar()}>Mudar de plano</Button>,
    ].filter(Boolean) : [<Button key="reativar" variant="outlined" onClick={acoes.mudarSituacao}>Reativar assinatura</Button>];
    const rodapeDoContrato = botoesDoContrato.length ? botoesDoContrato : null;

    const contrato = (
        <Cartao
            titulo="Contrato"
            rodape={rodapeDoContrato}
        >
            <Box component="dl" sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '170px minmax(0, 1fr)'}, rowGap: 1.5, columnGap: 2, m: 0}}>
                <Item rotulo="Empresa">
                    {licenca.empresa?.nome_fantasia || licenca.empresa?.razao_social || 'Empresa'}
                    <Typography component="span" variant="caption" color="text.secondary"> · {licenca.empresa?.cnpj || ''}</Typography>
                </Item>
                <Item rotulo="Plano">
                    <Link component={RouterLink} to={`/planos-comerciais/${licenca.id_plano}/versoes/${versao.numero}`}>{versao.nome_plano || licenca.plano?.nome_plano}, versão {versao.numero}</Link>
                    {situacaoVersao ? <Chip size="small" variant="outlined" sx={{ml: 1}} label={situacaoVersao[0]} color={situacaoVersao[1]} /> : null}
                    <Typography variant="caption" color="text.secondary" display="block">{formatReais(versao.preco_base_mensal)} por mês · {versao.modalidade === 'POS_PAGO' ? 'pós-pago' : 'pré-pago'}</Typography>
                    {podeMigrar ? (
                        <Acoes><BotaoLink onClick={() => acoes.trocar(versaoAVenda.id)}>Migrar para a versão {versaoAVenda.numero}, à venda</BotaoLink></Acoes>
                    ) : null}
                </Item>
                <Item rotulo="Tenant">
                    <Box component="span" sx={{fontFamily: 'monospace'}}>{licenca.tenant_id}</Box>
                    <Typography variant="caption" color="text.secondary" display="block">Gerado pelo backoffice. Não muda com o nome nem com o plano.</Typography>
                </Item>
                <Item rotulo="Desde">{dataBR(licenca.validade_inicio)}</Item>
                {licenca.status === 'TRIAL' && licenca.validade_fim ? (
                    <Item rotulo="Trial">
                        até {dataBR(licenca.validade_fim)}
                        <Typography variant="caption" color="text.secondary" display="block">No fim, a fatura de entrada cobra o proporcional até o fechamento da empresa.</Typography>
                    </Item>
                ) : null}
                <Item rotulo="Meio de pagamento">
                    {getMeioLabel(licenca.meio_pagamento)}
                    {licenca.status === 'AGUARDANDO_CARTAO' ? <Typography variant="caption" color="text.secondary" display="block">Aguardando a empresa cadastrar o cartão no checkout.</Typography> : null}
                    {ativa && (versao.meios_aceitos || []).length > 1 && licenca.status !== 'AGUARDANDO_CARTAO' ? <Acoes><BotaoLink onClick={acoes.trocarMeio}>Trocar o meio</BotaoLink></Acoes> : null}
                </Item>
                <Item rotulo="Desconto">
                    {desconto ? (
                        <>
                            {descricaoDoDesconto(desconto)}
                            {descontoVale(desconto) ? null : <Chip size="small" sx={{ml: 1}} label="vencido" />}
                            {ativa ? <Acoes><BotaoLink onClick={acoes.desconto}>Alterar</BotaoLink><BotaoLink color="error" onClick={acoes.removerDesconto}>Remover</BotaoLink></Acoes> : null}
                        </>
                    ) : (
                        <>
                            <Typography component="span" variant="body2" color="text.secondary">Nenhum</Typography>
                            {ativa ? <BotaoLink sx={{ml: 1, px: 0.5, minWidth: 0, textTransform: 'none'}} onClick={acoes.desconto}>Dar desconto</BotaoLink> : null}
                        </>
                    )}
                </Item>
                {licenca.troca_agendada ? (
                    <Item rotulo="Troca agendada">
                        Para {licenca.troca_agendada.versao?.nome_plano || 'outro plano'}, versão {licenca.troca_agendada.versao?.numero}, em {dataBR(licenca.troca_agendada.vale_a_partir_de)}, a virada do ciclo.
                        <Acoes><BotaoLink onClick={acoes.desfazerTroca}>Desfazer a troca</BotaoLink></Acoes>
                    </Item>
                ) : null}
                {(licenca.opcionais || []).some((item) => item.sai_apos) ? (
                    <Item rotulo="Opcionais saindo">
                        {(licenca.opcionais || []).filter((item) => item.sai_apos).map((item) => `${nomesDeModulo[item.id_modulo] || 'Módulo'} em ${dataBR(item.sai_apos)}`).join(', ')}.
                    </Item>
                ) : null}
                {licenca.cancelamento ? (
                    <Item rotulo="Cancelamento agendado">
                        Em {dataBR(licenca.cancelamento.agendado_para)}, o fim do ciclo pago. Até lá, o acesso continua; o excedente medido vai na fatura de encerramento.
                        {licenca.cancelamento.motivo ? <Typography variant="caption" color="text.secondary" display="block">Motivo: {licenca.cancelamento.motivo}</Typography> : null}
                        <Acoes><BotaoLink onClick={acoes.desistir}>Desistir do cancelamento</BotaoLink></Acoes>
                    </Item>
                ) : null}
            </Box>
        </Cartao>
    );

    const situacao = (
        <Cartao
            titulo="Situação e acesso"
            extra={<Chip size="small" label={getStatusLabel(licenca.status)} color={getStatusColor(licenca.status)} />}
            rodape={ativa ? <Button size="small" onClick={acoes.mudarSituacao}>Mudar a situação</Button> : null}
        >
            <Typography variant="body2">{acesso.texto}</Typography>
            {acesso.nivel === 'leitura' ? <Alert severity="warning" sx={{mt: 1.5}}>As funcionalidades de escrita estão bloqueadas. Veja em Direito e opcionais o que continua liberado.</Alert> : null}
            {acesso.nivel === 'nenhum' && licenca.status !== 'CANCELADA' ? <Alert severity="error" sx={{mt: 1.5}}>Ninguém da assinatura consegue entrar no sistema agora.</Alert> : null}
            <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 1.5}}>
                Quem muda a situação, quando uma cobrança vence, é a régua de inadimplência. A mudança manual fica no histórico, com o motivo.
            </Typography>
        </Cartao>
    );

    const fatura = (
        <Cartao titulo="Próxima fatura" extra={empresa?.id ? <Typography variant="caption" color="text.secondary">Fecha no dia {empresa.dia_fechamento} · vence no dia {empresa.dia_vencimento}</Typography> : null}>
            {previa.erro ? <Alert severity="warning">{previa.erro}</Alert> : null}
            {previa.valor ? (
                <ComposicaoDaFatura
                    previa={previa.valor}
                    rotuloDaSecao={(secao) => `${secao.plano} v${secao.versao}`}
                    rotuloDeFora={() => 'Esta assinatura'}
                />
            ) : null}
            {SEM_CICLO_PAGO.includes(licenca.status) ? null : (
                <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 1}}>
                    Prévia do fechamento, pelo mesmo cálculo da emissão. A empresa recebe uma fatura só, com todas as assinaturas.
                </Typography>
            )}
        </Cartao>
    );

    const linhaDoHistorico = (
        <Cartao titulo="Histórico">
            {!historico ? <Typography variant="body2" color="text.secondary">Não foi possível carregar o histórico agora.</Typography> : null}
            {historico && !linhas.length ? <Typography variant="body2" color="text.secondary">Nada registrado ainda.</Typography> : null}
            <Stack divider={<Box sx={{borderTop: 1, borderColor: 'divider'}} />}>
                {linhas.map((linha, index) => (
                    <Box key={`${linha.em}-${index}`} sx={{py: 1, display: 'grid', gridTemplateColumns: '92px minmax(0, 1fr)', columnGap: 1.5}}>
                        <Typography variant="caption" color="text.secondary">{dataBR(linha.em)}</Typography>
                        <Box>
                            <Typography variant="body2">{linha.texto}</Typography>
                            {linha.motivo ? <Typography variant="caption" color="text.secondary">{linha.motivo}</Typography> : null}
                        </Box>
                    </Box>
                ))}
            </Stack>
        </Cartao>
    );

    return (
        <Box sx={{display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.1fr) minmax(0, 1fr)'}, gap: 3, alignItems: 'start'}}>
            {contrato}
            <Stack spacing={3}>
                {situacao}
                {fatura}
                {linhaDoHistorico}
            </Stack>
        </Box>
    );
}
